import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { accountingUrl, payrollUrl } from 'src/environments/environment';
import { addBreadcrumb, reportError } from '../services/error-reporter';

/**
 * Reports accounting and payroll API calls that failed in a way the user cannot fix:
 * the server broke (5xx) or could not be reached at all (status 0 — timeout,
 * CORS, cold-start failure). 4xx are the user's input or the period lock and
 * are handled by their own screens; reporting them would only be noise.
 *
 * Never changes the error: it is passed on untouched.
 */
@Injectable()
export class ErrorReportInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const ours = req.url.startsWith(accountingUrl) || req.url.startsWith('altomateAccounting') || req.url.startsWith(payrollUrl);
    if (!ours || req.url.includes('/error-reports')) return next.handle(req);

    const path = req.url.replace(accountingUrl, '').replace(payrollUrl, '').split('?')[0];
    addBreadcrumb(`${req.method} ${path}`);
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && (err.status === 0 || err.status >= 500)) {
          const body: any = err.error;
          // A 5xx whose body is our own JSON ({ message } / { error }) was produced by a route's catch
          // block, and the backend's capture hook already recorded it with the real error and stack.
          // Reporting it again here only made one incident two problems. What the backend cannot
          // see is reported: no response at all (status 0) and platform errors (timeouts, crashes,
          // gateway pages), which are not our JSON.
          const handledByBackend = err.status >= 500 && body !== null && typeof body === 'object'
            && (typeof body.message === 'string' || typeof body.error === 'string');
          if (handledByBackend) return throwError(() => err);
          reportError({
            kind: 'http',
            message: `${req.method} ${path} failed: ${body?.message || body?.error || err.statusText || 'no response'}`,
            httpMethod: req.method,
            httpStatus: err.status
          });
        }
        return throwError(() => err);
      })
    );
  }
}
